import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Smile, 
  ShieldCheck, 
  Anchor, 
  Zap, 
  Layers, 
  Activity, 
  Sparkles, 
  CheckCircle2, 
  Award 
} from "lucide-react";
import image1 from '../../Assets/Services/Service10-2.jpg'
import image2 from '../../Assets/Services/Service10-3.jpg'
import { Helmet } from "react-helmet";

const AdvancedDentures = () => {
  return (
    <ServiceLayout
      title="Advanced Denture Solutions"
      subtitle="“Smile, speak, and enjoy your favorite foods with confidence again."
      heroImage="/images/hero/dentures.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <Helmet>
  <title>Advanced Dentures Vijayawada | Comfort Fit</title>
  <meta name="description" content="Modern advanced dentures in Vijayawada offering better comfort, chewing ability and improved aesthetics." />
  <meta name="keywords" content="advanced dentures Vijayawada" />
  <meta name="author" content="Dr Sridhar Dental" />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://sridhardental.com/service/advanced-dentures" />
</Helmet>
        <h3>Restore Your Smile with Advanced Denture Solutions</h3>
        <p className="text-lg text-gray-700 leading-relaxed">
          A confident smile can truly transform your life. At Dr. Sridhar International Dental Hospitals, we
provide advanced denture solutions designed for comfort, stability, and natural appearance.<br/>Our modern prosthodontic treatments help restore your ability to eat, speak, and smile confidently
again, using high-quality materials and expert care for long-lasting results.
        </p>
      </section>

      {/* Main Feature: BPS & Implant Supported */}
      <section className="grid md:grid-cols-2 gap-8 mb-16">
        {/* BPS Dentures */}
        <div className="bg-blue-900 text-white p-8 rounded-3xl shadow-xl border border-blue-800">
          <Award className="text-blue-300 w-12 h-12 mb-4" />
          <h3 className="text-2xl font-bold mb-4 !text-blue-300">BPS Dentures</h3>
          <p className="!text-blue-100 text-sm mb-6 leading-relaxed">
            BPS (Biofunctional Prosthetic System) dentures are designed using highly precise techniques to
provide a natural fit, superior comfort, and excellent stability.
          </p>
          <ul className="space-y-2 text-sm">
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400"/> Premium quality materials</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400"/> Excellent comfort and retention</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400"/> Highly natural tooth aesthetics</li>
            <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-400"/> Precise jaw relation technology</li>
          </ul>
        </div>

        {/* Implant Supported */}
        <div className="bg-white border-2 border-blue-600 p-8 rounded-3xl shadow-lg">
          <Anchor className="text-blue-600 w-12 h-12 mb-4" />
          <h3 className="text-2xl font-bold text-blue-900 mb-4">Implant-Supported Dentures</h3>
          <p className="text-gray-600 text-sm mb-6 leading-relaxed">
            Anchored securely using dental implants placed in the jawbone. These offer exceptional stability compared to traditional removable dentures.
          </p>
          <ul className="space-y-2 text-sm text-gray-700">
            <li className="flex items-center gap-2 font-medium"><Zap className="w-4 h-4 text-blue-600"/> No slipping or movement</li>
            <li className="flex items-center gap-2 font-medium"><Zap className="w-4 h-4 text-blue-600"/> Maximum chewing efficiency</li>
            <li className="flex items-center gap-2 font-medium"><Zap className="w-4 h-4 text-blue-600"/> Maintains jaw bone structure</li>
            <li className="flex items-center gap-2 font-medium"><Zap className="w-4 h-4 text-blue-600"/> Long-lasting stability</li>
          </ul>
        </div>
      </section>

      {/* Complete Dentures Section */}
      <section className="mb-16 bg-blue-50 p-8 rounded-3xl border border-blue-100">
        <div className="flex flex-col md:flex-row gap-8 items-center">
          <div className="md:w-2/3">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Complete Dentures</h2>
            <p className="text-gray-700 mb-6">
              Used when all natural teeth are missing in the upper jaw, lower jaw, or both. These restore your smile and help regain normal chewing and speaking ability.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                "Restores facial appearance",
                "Natural-looking gum design",
                "Improves speech efficiency",
                "Custom-made for comfortable fit"
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm border border-blue-50">
                  <ShieldCheck className="text-blue-500 w-5 h-5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="md:w-1/3 text-center bg-white p-6 rounded-2xl border border-blue-100 italic text-blue-800 font-medium">
            “Crafted with precision for comfort, stability, and a natural smile.”
          </div>
        </div>
      </section>

      {/* Other Denture Solutions Grid */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Partial Denture Solutions</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Cast Partial Dentures */}
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <Layers className="text-blue-600 w-10 h-10 mb-4" />
            <h4 className="font-bold text-blue-900 mb-2">Cast Partial (CPD)</h4>
            <p className="text-sm text-gray-600 mb-4">Uses a strong metal framework to support artificial teeth while preserving your remaining natural teeth.</p>
            <span className="text-xs font-bold uppercase text-blue-500">Long-lasting durability</span>
          </div>

          {/* Flexible Dentures */}
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <Activity className="text-blue-600 w-10 h-10 mb-4" />
            <h4 className="font-bold text-blue-900 mb-2">Flexible Dentures</h4>
            <p className="text-sm text-gray-600 mb-4">Made from lightweight thermoplastic materials. Metal-free design for superior comfort and aesthetics.</p>
            <span className="text-xs font-bold uppercase text-blue-500">Lightweight & Comfortable</span>
          </div>

          {/* RPD */}
          <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition">
            <CheckCircle2 className="text-blue-600 w-10 h-10 mb-4" />
            <h4 className="font-bold text-blue-900 mb-2">Removable Partial (RPD)</h4>
            <p className="text-sm text-gray-600 mb-4">An affordable solution that replaces several missing teeth and prevents shifting of natural teeth.</p>
            <span className="text-xs font-bold uppercase text-blue-500">Easy to Clean</span>
          </div>
        </div>
      </section>

      {/* Why Us Footer */}
      <section className="border-t pt-12 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-8">Why Choose Our Denture Solutions?</h2>
        <div className="flex flex-wrap justify-center gap-6">
          {[
            {  label: "Natural-Looking Aesthetics",  },
            {  label: "Premium International Materials" },
            {  label: "Customized Precision Fit" },
            {  label: "Improved Chewing Comfort" }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 bg-gray-50 px-5 py-2 rounded-full border border-gray-200">
              <span className="text-blue-600 w-4 h-4">{item.icon}</span>
              <span className="text-sm font-bold text-gray-700">{item.label}</span>
            </div>
          ))}
        </div>
      </section>
    </ServiceLayout>
  );
};

export default AdvancedDentures;